import { applyLayout } from '../domain/active-layout.js';
import { hypot } from "yage/utils/deterministicMath";
import type { GameModel, ReadOnlyGameModel } from "yage/game/GameModel";
import { EntityFactory } from "yage/entity/EntityFactory";
import { Transform } from "yage/schemas/entity/Transform";
import { Locomotion } from "yage/schemas/entity/Locomotion";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { InputManager } from "yage/inputs/InputManager";
import { AirportState, CareerState, AirportStation, AirportCash, AirportAgent, AirportPath, AirportBag, AirportStaff, AirportZone, AirportVending, AirportTrash, AirportPlane, AirportPilot, AirportRoute, AirportPassenger } from "../components/index.js";
import { boardingWorkPoint } from "../domain/queues.js";
import { CONFIG, binPad, SPAWN, STREET_ENTRY, ENTRANCE_DOOR, ENTRANCE, SPECIAL, stationPad, cashPoint, VENDING, BINS, vendingUpgradePoint, QUEUE_PATIENCE } from "../domain/config.js";
import { aircraftStand } from "../domain/gateflow.js";
import { freshPlane, freshOperations } from "../domain/operations.js";
import { AIRPORTS, airlineMode, stationAllowed, vendingAllowed, tier } from "../domain/airports.js";
import { navFor, gateLayout, obstaclesFor, hoardingFor, allHoarding } from "../domain/layout.js";
import { RigidBox } from "yage/schemas/physics/RigidBox";
import { freshTerminalEconomy, freshWallet, freshStation } from "../domain/save.js";
import { allocateStaffId, airportRun, type ActorRef } from "../domain/terminal-record.js";
import { TIMETABLE_KEYS } from "../domain/timetable.js";
import { buildTerminal } from "./terminal.js";
import { catchUpRetainedAirports } from "./retained.js";
import type { SavedCaptain, Point, PadKind } from "../domain/types.js";
import type { SaveData } from "../domain/save.js";
/** The first airport's grid, kept for tools and tests; live code asks navFor(airportId). */
export const NAV = navFor(AIRPORTS[0].code);
export const ids = (g: GameModel | ReadOnlyGameModel, c: any): number[] => [...g.getComponentActives(c)].sort((a, b) => a - b);
export const state = (g: GameModel | ReadOnlyGameModel) => g.getTypedUnsafe(AirportState, g.coreEntity);
/** Career-owned state: the one wallet, reputation and identity, separate from the current terminal. */
export const career = (g: GameModel | ReadOnlyGameModel) => g.getTypedUnsafe(CareerState, g.coreEntity);
export const stationIds = (g: GameModel | ReadOnlyGameModel): number[] => ids(g, AirportStation).map(id => [g.getTypedUnsafe(AirportStation, id).index, id]).sort((a, b) => a[0] - b[0] || a[1] - b[1]).map(([, id]) => id);
export const stations = (g: GameModel | ReadOnlyGameModel) => stationIds(g).map(id => g.getTypedUnsafe(AirportStation, id));
export const vending = (g: GameModel | ReadOnlyGameModel) => ids(g, AirportVending).map(id => g.getTypedUnsafe(AirportVending, id)).sort((a, b) => a.index - b.index);
export const distance = (a: Point, b: Point) => hypot(a.x - b.x, a.y - b.y);
export function make(g: GameModel, name: string, overrides: Record<string, unknown> = {}): number { return EntityFactory.getInstance().generateEntity(g, name, overrides); }
/** Routine flight chatter must not hide purchases or objective completion. */
export function notice(g: GameModel, text: string, priority = 1) {
    const s = state(g);
    if (s.noticeUntil > g.frame && s.noticePriority > priority) return;
    s.notice = text; s.noticeUntil = g.frame + (priority >= 2 ? 300 : 240); s.noticePriority = priority; s.revision++;
}
export function feedback(g: GameModel, id: number, text: string) { const a = g.getTypedUnsafe(AirportAgent, id); a.feedback = text; a.feedbackUntil = g.frame + 150; }
export function route(g: GameModel, id: number, target: Point, force = false): boolean {
    const p = g.getTypedUnsafe(AirportPath, id), t = g.getTypedUnsafe(Transform, id);
    if (!force && Math.abs(p.targetX - target.x) < 1 && Math.abs(p.targetY - target.y) < 1)
        return true;
    const port = tier(state(g)), path = navFor(port.code, gateCount(g)).path(t, target, port.maxX);
    p.targetX = target.x;
    p.targetY = target.y;
    p.points = path;
    p.cursor = 0;
    p.moving = path.length > 0;
    p.stuck = 0;
    p.revision++;
    return path.length > 0;
}
/** Off the terminal floor (out to the aircraft): straight to `target`, with no navigation grid to consult. */
export function walkStraight(g: GameModel, id: number, target: Point) {
    const p = g.getTypedUnsafe(AirportPath, id);
    if (Math.abs(p.targetX - target.x) < 1 && Math.abs(p.targetY - target.y) < 1 && p.points.length) return;
    Object.assign(p, { targetX: target.x, targetY: target.y, points: [target.x, target.y], cursor: 0, moving: true, stuck: 0, revision: p.revision + 1 });
}
export function stop(g: GameModel, id: number) { const p = g.getTypedUnsafe(AirportPath, id); p.points = []; p.cursor = 0; p.moving = false; const v = g.getTypedUnsafe(Locomotion, id); v.x = 0; v.y = 0; }
export function buildWorld(g: GameModel, initial?: {save: SaveData | null; elapsedMs: number;careerId?:string;relaxed?:boolean}) {
    Object.assign(state(g), freshTerminalEconomy());
    Object.assign(career(g), freshWallet());
    applyLayout(tier(state(g)).code);
    buildLayout(g, true);
    if (initial) {
        if (initial.careerId) career(g).careerId = initial.careerId;
        state(g).loaded = true;
        state(g).loadFrame = g.frame;
        state(g).relaxed = !!initial.relaxed;
        if (initial.save) restoreAirport(g, initial.save, initial.elapsedMs);
    }
}
/** Authored terminal entities only: never touches the wallet or other economy state. */
export function buildLayout(g: GameModel, starterCrew: boolean) {
    CONFIG.stages.forEach((s, index) => {
        make(g, 'Station', { AirportStation: freshStation(index), Transform: { x: s.x, y: s.y } });
        for (const kind of ['work', 'build', 'hire', 'upgrade', 'divider'] as PadKind[])
            make(g, 'Zone', { AirportZone: { kind, stage: index }, Transform: stationPad(index, kind) });
        make(g, 'Cash', { AirportCash: { stage: index }, Transform: cashPoint(index) });
        if (starterCrew && s.starterCrew) hireStaff(g, index);
    });
    for (const p of SPECIAL)
        make(g, 'Zone', { AirportZone: { kind: p.kind, stage: -1, radius: p.kind === 'belt' ? 65 : 46 }, Transform: { x: p.x, y: p.y } });
    buildWalls(g);
    VENDING.forEach((v, index) => {
        make(g, 'Vending', { AirportVending: { index }, Transform: { x: v.x, y: v.y } });
        make(g, 'Cash', { AirportCash: { stage: -1, vending: index }, Transform: { x: v.x + 56, y: v.y + 64 } });
        make(g, 'Zone', { AirportZone: { kind: 'vending', stage: index, radius: 38 }, Transform: vendingUpgradePoint(index) });
    });
    BINS.forEach(p => make(g, 'Zone', { AirportZone: { kind: 'trash', stage: -1 }, Transform: binPad(p) }));
    freshOperations().routes.forEach(r => make(g, "Route", { AirportRoute: r }));
    syncGates(g);
}
/** Static colliders for the current airport's floor plan (its own gate row changes the airside wall). */
function buildWalls(g: GameModel) {
    // Transform is already a center. Never add half-extents here.
    for (const r of obstaclesFor(tier(state(g)).code, gateCount(g)))
        make(g, 'Wall', { Transform: { x: r.x, y: r.y }, RigidBox: { width: r.w, height: r.h, angle: 0, point: { x: 0, y: 0 } }, Locomotion: { directionX: 1, directionY: 0 } });
}
/**
 * Moving to an airport with a different floor plan: make its plan active, then rebuild every placed piece (desks,
 * pads, cash, vending, walls) where that plan puts them. Station and vending state starts fresh, as a move does.
 */
export function relayoutAirport(g: GameModel) {
    applyLayout(tier(state(g)).code);
    placeLayout(g);
}
/**
 * Move every placed piece (desks, pads, counter and vending cash, machines, bins, walls, gate desks) to where the
 * active floor plan puts it, keeping each one's state. Used whenever the airport's floor plan changes under a world.
 */
export function placeLayout(g: GameModel) {
    const move = (id: number, p: Point) => Object.assign(g.getTypedUnsafe(Transform, id), { x: p.x, y: p.y });
    for (const id of ids(g, AirportStation)) { const s = CONFIG.stages[g.getTypedUnsafe(AirportStation, id).index]; move(id, s); }
    for (const id of ids(g, AirportZone)) {
        const z = g.getTypedUnsafe(AirportZone, id);
        if (z.kind === 'trash') { g.removeEntity(id); continue; }
        if (z.kind === 'vending') { move(id, vendingUpgradePoint(z.stage)); continue; }
        if (z.stage >= 0 && ['work', 'build', 'hire', 'upgrade', 'divider'].includes(z.kind)) { if (!(z.kind === 'work' && z.stage === 6)) move(id, stationPad(z.stage, z.kind as PadKind)); continue; }
        const pad = SPECIAL.find(p => p.kind === z.kind);
        if (pad) move(id, pad);
    }
    BINS.forEach(p => make(g, 'Zone', { AirportZone: { kind: 'trash', stage: -1 }, Transform: binPad(p) }));
    for (const id of ids(g, AirportCash)) {
        const c = g.getTypedUnsafe(AirportCash, id);
        if (c.vending >= 0) { const v = VENDING[c.vending]; move(id, { x: v.x + 56, y: v.y + 64 }); } else move(id, cashPoint(c.stage));
    }
    for (const id of ids(g, AirportVending)) move(id, VENDING[g.getTypedUnsafe(AirportVending, id).index]);
    rebuildWalls(g);
    syncGates(g);
}
/** Moving to an airport with a different floor plan swaps the static colliders; captains are never touched. */
export function rebuildWalls(g: GameModel) {
    // Only static walls: people and their physics bodies stay where they are.
    for (const id of ids(g, RigidBox)) if (g.getTypedUnsafe(RigidBox, id).isStatic && !g.hasComponent(AirportAgent, id)) g.removeEntity(id);
    buildWalls(g);
}
/** Give a newly created staff body the next persisted record identity. */
export function assignStaffRecord(g: GameModel, id: number): number {
    const e = state(g), next = allocateStaffId(e);
    if (!next) throw new Error('Staff identity allocator is exhausted.');
    g.getTypedUnsafe(AirportStaff, id).recordId = next.id;
    e.nextStaffId = next.nextStaffId;
    return id;
}
/** The creator restores once before joining captains or advancing simulation. */
export function restoreAirport(g: GameModel, save: SaveData, _elapsedMs = 0) {
    const e = state(g), frame = g.frame;
    // R2-03: an unoccupied Act I airport freezes; no offline income is granted.
    e.run = airportRun(save.career.currentAirportId);
    Object.assign(career(g), save.career.wallet, { advance: save.career.wallet.advance ?? 0, diary: JSON.stringify(save.career.diary ?? []), offlineAward: 0, offlineClaimed: true, careerId: save.careerId, saveGameTime: save.gameTime,
        savedCaptains: JSON.stringify(save.career.captains.map(c => ({personId:c.personId,radioCooldown:frame+c.radioCooldown}))),
        retainedAirports: JSON.stringify(catchUpRetainedAirports(save.airports.slice(1), _elapsedMs, save.career.wallet.prestige)) });
    const record = structuredClone(save.airports[0]);
    // A captain who is not in this room drops what they carried in place, exactly as leaving does.
    const present = new Set(ids(g, AirportAgent).map(id => g.getTypedUnsafe(AirportAgent, id).personId));
    const drop = (carrier: ActorRef | null) => carrier?.kind === 'person' && !present.has(carrier.id) ? null : carrier;
    for (const item of [...record.bags, ...record.litter]) item.carrier = drop(item.carrier);
    // A room boots near frame 0; a past event older than that is placed at frame 0, never at the -1 unset sentinel.
    for (const plane of record.operations.planes) {
        plane.nextDepartureFrame = Math.max(plane.nextDepartureFrame, -frame);
        for (const key of TIMETABLE_KEYS) if (plane[key] !== null) plane[key] = Math.max(plane[key]!, -frame);
    }
    const built = buildTerminal(g, record);
    if (!built.ok) throw new Error('A validated career could not rebuild its terminal: ' + built.error);
    for (const id of ids(g, AirportAgent)) {
        const a = g.getTypedUnsafe(AirportAgent,id);
        const saved = save.career.captains.find(c=>c.personId===a.personId);
        a.radioCooldown = frame + (saved?.radioCooldown??0);
        a.upgradeTicket = '';a.ticketCredit = 0;a.ticketCost = 0;a.inputLocked = false;
    }
}
export function joinPlayer(g: GameModel, pid: string, config?: {personId?:string} | null): number {
    const color = ids(g, AirportAgent).length % 4;
    const opening = ids(g, AirportAgent).length === 0 && state(g).run === 1 && state(g).mission === 0 && state(g).flights === 0;
    const target = { x: SPAWN.x + color * 34, y: SPAWN.y };
    const personId = config?.personId || 'captain-' + (color+1);
    const saved = (JSON.parse(career(g).savedCaptains) as SavedCaptain[]).find(c=>c.personId===personId);
    const id = make(g, 'Player', { AirportAgent: { color, personId, radioCooldown:Math.max(g.frame,saved?.radioCooldown??0), entering: opening }, Transform: opening ? { x: ENTRANCE.x + 24, y: STREET_ENTRY.y } : target, AirportPath: {
        targetX: target.x, targetY: target.y,
        moving: opening,
        points: opening ? [ENTRANCE.x + 24, ENTRANCE_DOOR.y, ENTRANCE.x + 24, ENTRANCE.y, target.x, target.y] : [],
    } });
    const p = g.getTypedUnsafe(PlayerInput, id);
    p.pid = pid;
    p.name = 'Captain ' + (color + 1);
    p.keyMap = InputManager.buildKeyMap();
    return id;
}
export function leavePlayer(g: GameModel, pid: string) {
    for (const id of ids(g, AirportAgent)) {
        if (g.getTypedUnsafe(PlayerInput, id).pid !== pid)
            continue;
        for (const bag of [...ids(g, AirportBag), ...ids(g, AirportTrash)]) {
            const b = g.hasComponent(AirportBag, bag) ? g.getTypedUnsafe(AirportBag, bag) : g.getTypedUnsafe(AirportTrash, bag);
            if (b.carrier === id) {
                b.carrier = -1;
                Object.assign(g.getTypedUnsafe(Transform, bag), { x: g.getTypedUnsafe(Transform, id).x, y: g.getTypedUnsafe(Transform, id).y });
            }
        }
        const agent = g.getTypedUnsafe(AirportAgent,id), e = state(g);
        const saved = JSON.parse(career(g).savedCaptains) as SavedCaptain[];
        const other = saved.filter(c=>c.personId!==agent.personId);
        other.push({personId:agent.personId,radioCooldown:agent.radioCooldown});
        career(g).savedCaptains = JSON.stringify(other);
        g.removeEntity(id);
    }
}
/** Fresh patience whenever a passenger joins (or rejoins) a service queue. */
export function resetPatience(p: AirportPassenger) { p.patience = QUEUE_PATIENCE; p.leaving = 0; p.queueOrder = -1; }
export function hireStaff(g: GameModel, index: number): number {
    const e = state(g);
    // The first hire of a fresh career is a fixed-desk clerk; later hires roam every desk.
    const role = e.run === 1 && ids(g, AirportStaff).length === 0 ? 'clerk' : 'crew';
    return assignStaffRecord(g, make(g, 'Staff', { AirportStaff: { stage: index, homeStage: index, role }, Transform: { x: ENTRANCE.x, y: ENTRANCE.y }, AirportPath: { speed: 2.1 } }));
}
/** A specialist (baggage handler, cleaner or cash runner) walks in through the front door with no home desk. */
export function hireSpecialist(g: GameModel, role: 'porter' | 'cleaner' | 'runner'): number {
    return assignStaffRecord(g, make(g, 'Staff', { AirportStaff: { stage: -1, homeStage: -1, role }, Transform: { x: ENTRANCE.x, y: ENTRANCE.y }, AirportPath: { speed: 2.1 } }));
}
/** A cleaner bins litter but never earns the captain's cleanup bonus. */
export function hireCleaner(g: GameModel): number { return hireSpecialist(g, 'cleaner'); }
export function zoneAvailable(g: GameModel | ReadOnlyGameModel, id: number): boolean {
    const z = g.getTypedUnsafe(AirportZone, id);
    const e = state(g);
    if (z.kind === 'vending') return vendingAllowed(e, z.stage);
    if (z.kind === 'pilots' || z.kind === 'routes') return airlineMode(e);
    if (z.kind === 'chairs') return tier(e).chairs;
    if (z.stage < 0 && (g.getTypedUnsafe(Transform, id).x > tier(e).maxX)) return false;
    if (z.stage < 0)
        return true;
    if (!stationAllowed(e, z.stage)) return false;
    const ss = stations(g), s = ss[z.stage];
    if (z.kind === 'work' && z.stage === 6 && z.gate >= s.lanes) return false;
    if (z.kind === 'build')
        return !s.unlocked;
    if (!s.unlocked)
        return false;
    if (z.kind === 'hire')
        return !s.manager;
    return true;
}
export function zoneId(g: GameModel | ReadOnlyGameModel, kind: PadKind, index = -1): number { return ids(g, AirportZone).find(id => { const z = g.getTypedUnsafe(AirportZone, id); return z.kind === kind && z.stage === index; }) ?? -1; }

/** One real aircraft per purchased gate, not just a hidden capacity multiplier. */
/** Gates bought at this airport (Boarding's lanes). */
export function gateCount(g: GameModel | ReadOnlyGameModel): number { return stations(g)[6]?.lanes ?? 1; }
export function syncGates(g: GameModel) {
    const count = gateCount(g);
    // Buying a gate takes down the hoarding it opens: its door, and the walls of every area on the way to it.
    const code = tier(state(g)).code, standing = hoardingFor(code, count), down = allHoarding(code).filter(r => !standing.some(s => s.x === r.x && s.y === r.y && s.w === r.w && s.h === r.h));
    if (down.length) for (const id of ids(g, RigidBox)) {
        const box = g.getTypedUnsafe(RigidBox, id), at = g.getTypedUnsafe(Transform, id);
        if (box.isStatic && !g.hasComponent(AirportAgent, id) && down.some(r => r.x === at.x && r.y === at.y && r.w === box.width && r.h === box.height)) g.removeEntity(id);
    }
    for (let gate = 0; gate < count; gate++) {
        const zone = ids(g, AirportZone).find(id => { const z = g.getTypedUnsafe(AirportZone, id); return z.kind === 'work' && z.stage === 6 && z.gate === gate; });
        const point = boardingWorkPoint(gate, tier(state(g)).code);
        if (zone === undefined) make(g, 'Zone', { AirportZone: { kind: 'work', stage: 6, gate }, Transform: point });
        else Object.assign(g.getTypedUnsafe(Transform, zone), point);
        if (ids(g, AirportPlane).some(id => g.getTypedUnsafe(AirportPlane, id).gate === gate)) continue;
        const plane = freshPlane(gate);
        const component: AirportPlane = {
            boardingStartedFrame: -1, departureReadyFrame: -1, boardingAnnouncedFrame: -1, boardingDeskFrames: 0, unattendedSince: -1, aircraftReadyFrame: 0, scheduledDepartureFrame: -1, boardingOpensFrame: -1, scheduledArrivalFrame: -1, expectedArrivalFrame: -1, departureOffset: 0, arrivalOffset: 0,
            boardingCalled: false,
            visiting: !airlineMode(state(g)),
            gate: plane.gate,
            phase: plane.phase,
            clock: plane.clock,
            boarded: plane.boarded,
            seats: airlineMode(state(g)) ? plane.seats : tier(state(g)).seats,
            flightNumber: plane.flightNumber,
            routeIndex: plane.routeIndex,
            pilot: -1,
            tripFrames: airlineMode(state(g)) ? plane.tripFrames : tier(state(g)).turnaround,
            scheduleFrames: plane.scheduleFrames,
            nextDepartureFrame: plane.nextDepartureFrame,
            plannedRoute: plane.plannedRoute,
            plannedSchedule: plane.plannedSchedule,
        };
        make(g, 'Plane', { AirportPlane: component, Transform: aircraftStand(tier(state(g)).code, gate) });
    }
}
export function litter(g: GameModel, point: Point, serial: number) {
    if (ids(g, AirportTrash).length >= 80) return;
    const desired = { x: point.x + (serial % 3 - 1) * 30, y: point.y + 40 + (serial % 4) * 14 };
    const nav = navFor(tier(state(g)).code, gateCount(g)), index = nav.nearest(desired);
    if (index < 0) return;
    make(g, 'Trash', { AirportTrash: { serial, kind: serial % 3 }, Transform: nav.clear(desired) ? desired : nav.point(index) });
}
